import {
  matrizToNumber,
  matrixViewFromSavedLineas,
  normStr,
} from "../Produccion/a3PedidoMatrizUtils";
import {
  apiDateFromInput,
  inputDate,
  parseOptionalDecimal,
  parseOptionalInt,
} from "./logisticaUtils";

function dashText(v) {
  const s = String(v ?? "").trim();
  return s || "—";
}

export function formatLogisticaSummaryDate(v) {
  const d = inputDate(v);
  if (!d) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  if (!match) return d;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

function dashDate(v) {
  return dashText(formatLogisticaSummaryDate(v));
}

/** Lee «Factory: …» de Remarks (texto legado) sin tocar el resto. */
export function factoryNameFromRemarks(remarks) {
  const text = String(remarks || "");
  const match = text.match(/Factory\s*:\s*(.+)/i);
  if (!match) return "";
  return String(match[1] || "")
    .replace(/^[•*\-–]+\s*/, "")
    .split(/\r?\n/)[0]
    .trim();
}

/** Fábricas sugeridas desde ProductionLogistics de los pedidos (sin API extra). */
export function factorySuggestionsFromConsolidacion(consolidacion) {
  const names = [];
  const seen = new Set();
  for (const row of consolidacion?.porPedido || []) {
    const factory = String(row?.productionLogistics?.Factory || "").trim();
    if (!factory) continue;
    const key = factory.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(factory);
  }
  return names;
}

export function collectPedidoProveedorKeys(
  pedidoLinks = [],
  extraPedido = null,
) {
  const codPros = [];
  const nombres = [];
  const seenCod = new Set();
  const seenNom = new Set();
  const add = (pedido) => {
    const cod = String(pedido?.CodPro ?? "").trim();
    if (cod) {
      const key = cod.toLowerCase();
      if (!seenCod.has(key)) {
        seenCod.add(key);
        codPros.push(cod);
      }
    }
    const nom = String(
      pedido?.ProveedorNombre || pedido?.NomPro || pedido?.SupplierName || "",
    ).trim();
    if (nom) {
      const key = nom.toLowerCase();
      if (!seenNom.has(key)) {
        seenNom.add(key);
        nombres.push(nom);
      }
    }
  };
  for (const lnk of Array.isArray(pedidoLinks) ? pedidoLinks : []) {
    add(lnk?.pedidoA3);
  }
  if (extraPedido) add(extraPedido);
  return { codPros, nombres };
}

export function factoryNamesFromProveedorRows(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) =>
      String(
        row?.NombreFabrica ?? row?.name ?? (typeof row === "string" ? row : ""),
      ).trim(),
    )
    .filter(Boolean);
}

export function mergeFactorySelectOptions(...lists) {
  const names = [];
  const seen = new Set();
  for (const list of lists) {
    for (const value of Array.isArray(list) ? list : []) {
      const name = String(value || "").trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      names.push(name);
    }
  }
  return names;
}

/** Resumen de cabecera agrupado como la logística legacy. */
export function buildEnvioHeaderSummaryGroups(
  header = {},
  { methodLabel } = {},
) {
  const groups = [
    {
      id: "production",
      title: "Producción",
      items: [
        { label: "Factory", value: dashText(header.Factory) },
        { label: "PO", value: dashText(header.POReference) },
        { label: "PO Date", value: dashDate(header.PODate) },
        { label: "Inspection", value: dashDate(header.InspectionDate) },
      ],
    },
    {
      id: "cargo",
      title: "Carga",
      items: [
        { label: "Cartons", value: dashText(header.NCartons) },
        { label: "Kg", value: dashText(header.GrossWeightKgs) },
        { label: "CBM", value: dashText(header.TotalVolumeCbm) },
      ],
    },
    {
      id: "shipment",
      title: "Envío",
      items: [
        {
          label: "Método",
          value: dashText(methodLabel || header.ShipmentMethod),
        },
        { label: "Forwarder", value: dashText(header.Forwarder) },
        { label: "Booking", value: dashDate(header.BookingDone) },
        { label: "ETD", value: dashDate(header.ETD) },
        { label: "ExFD", value: dashDate(header.ExFD) },
        { label: "ETDF", value: dashDate(header.ETDF) },
        { label: "CUT-OFF", value: dashDate(header.CutOff) },
      ],
    },
    {
      id: "tracking",
      title: "Tracking",
      items: [
        { label: "Code", value: dashText(header.ShipmentCodeCustomer) },
        { label: "B/L AWB", value: dashText(header.BLAWBNr) },
        { label: "ETA", value: dashDate(header.ETA) },
        { label: "DD", value: dashDate(header.DD) },
      ],
    },
    {
      id: "documents",
      title: "Documentos",
      items: [
        { label: "Docs OK", value: dashDate(header.DocumentsApproved) },
        { label: "Telex", value: dashDate(header.OriginalTelexDocsSemt) },
      ],
    },
  ];
  return groups.map((group) => {
    const filled = group.items.filter(
      (item) => item.value && item.value !== "—",
    ).length;
    return { ...group, filled, total: group.items.length };
  });
}

/** Parte testeable de getShipmentMethodMeta (sin iconos React). */
export function resolveShipmentMethodDisplay(method) {
  const raw = String(method ?? "").trim();
  const normalized = raw.toLowerCase();
  const isSeaAir =
    normalized.includes("sea-air") ||
    normalized.includes("sea air") ||
    normalized.includes("sea/air") ||
    normalized.includes("air/sea") ||
    normalized.includes("air-sea");

  if (isSeaAir) {
    return { label: raw || "Sea-Air", color: "#14b8a6", kind: "seaAir" };
  }
  if (normalized.includes("courier") || normalized.includes("mensaj")) {
    return { label: raw || "Courier", color: "#0f766e", kind: "courier" };
  }
  if (
    normalized.includes("air") ||
    normalized.includes("aer") ||
    normalized.includes("flight")
  ) {
    return { label: raw || "Air", color: "#0ea5e9", kind: "air" };
  }
  if (
    normalized.includes("sea") ||
    normalized.includes("mar") ||
    normalized.includes("boat")
  ) {
    return { label: raw || "Sea", color: "#2563eb", kind: "sea" };
  }
  if (
    normalized.includes("comb") ||
    normalized.includes("mix") ||
    normalized.includes("multi")
  ) {
    return {
      label: raw || "Combination",
      color: "#8b5cf6",
      kind: "combination",
    };
  }
  return { label: raw || "Truck", color: "#ea580c", kind: "truck" };
}

export function totalUnitsFromEnvioLineas(lineas) {
  const mv = matrixViewFromSavedLineas(lineas);
  if (mv) return mv.total;
  const arr = Array.isArray(lineas) ? lineas : [];
  return arr.reduce(
    (acc, l) => acc + matrizToNumber(l.Unidades ?? l.unidades),
    0,
  );
}

export function lineasToState(lineasRaw) {
  const src = Array.isArray(lineasRaw) ? lineasRaw : [];
  return src.map((l) => ({
    codFamTallaH: String(l.CodFamTallaH ?? l.codFamTallaH ?? "").trim(),
    codTallaH: String(l.CodTallaH ?? l.codTallaH ?? "").trim(),
    codFamTallaV: String(l.CodFamTallaV ?? l.codFamTallaV ?? "").trim(),
    codTallaV: String(l.CodTallaV ?? l.codTallaV ?? "").trim(),
    unidades: matrizToNumber(l.Unidades ?? l.unidades),
  }));
}

export function envioSupportsMatrixEdit(lineas) {
  return Boolean(matrixViewFromSavedLineas(lineas));
}

export function buildEnvioHeaderUpdatePayload(header = {}) {
  return {
    ETA: apiDateFromInput(header.ETA),
    Forwarder: normStr(header.Forwarder) || null,
    POReference: normStr(header.POReference) || null,
    PODate: apiDateFromInput(header.PODate),
    Factory: normStr(header.Factory) || null,
    Remarks: normStr(header.Remarks) || null,
    LinkDocs: normStr(header.LinkDocs) || null,
    ShipmentMethod: normStr(header.ShipmentMethod) || null,
    NCartons: parseOptionalInt(header.NCartons),
    GrossWeightKgs: parseOptionalDecimal(header.GrossWeightKgs),
    TotalVolumeCbm: parseOptionalDecimal(header.TotalVolumeCbm),
    BookingDone: apiDateFromInput(header.BookingDone),
    InspectionDate: apiDateFromInput(header.InspectionDate),
    ExFD: apiDateFromInput(header.ExFD),
    ETDF: apiDateFromInput(header.ETDF),
    CutOff: apiDateFromInput(header.CutOff),
    ETD: apiDateFromInput(header.ETD),
    DD: apiDateFromInput(header.DD),
    ShipmentCodeCustomer: normStr(header.ShipmentCodeCustomer) || null,
    BLAWBNr: normStr(header.BLAWBNr) || null,
    DocumentsApproved: apiDateFromInput(header.DocumentsApproved),
    OriginalTelexDocsSemt: apiDateFromInput(header.OriginalTelexDocsSemt),
  };
}

export function buildEnvioLineasSavePayload(lineasTallas = []) {
  return (Array.isArray(lineasTallas) ? lineasTallas : [])
    .map((l) => ({
      codFamTallaH: l.codFamTallaH || null,
      codTallaH: l.codTallaH || null,
      codFamTallaV: l.codFamTallaV || null,
      codTallaV: l.codTallaV || null,
      unidades: matrizToNumber(l.unidades),
    }))
    .filter(
      (l) =>
        l.codFamTallaH &&
        l.codTallaH &&
        l.codFamTallaV &&
        l.codTallaV &&
        l.unidades >= 0,
    );
}

export function lineaEnvioMergeKey(l) {
  return [
    String(l?.CodFamTallaH ?? l?.codFamTallaH ?? "")
      .trim()
      .toUpperCase(),
    String(l?.CodTallaH ?? l?.codTallaH ?? "")
      .trim()
      .toUpperCase(),
    String(l?.CodFamTallaV ?? l?.codFamTallaV ?? "")
      .trim()
      .toUpperCase(),
    String(l?.CodTallaV ?? l?.codTallaV ?? "")
      .trim()
      .toUpperCase(),
  ].join("|");
}

/** Suma líneas de varios pedidos del envío (misma clave H/V) para la matriz consolidada. */
export function mergeMultiPedidoLineasPayload(pedidoLineasByPedido = {}) {
  const merged = new Map();
  for (const arr of Object.values(pedidoLineasByPedido || {})) {
    for (const l of Array.isArray(arr) ? arr : []) {
      const k = lineaEnvioMergeKey(l);
      if (!k) continue;
      const uds = matrizToNumber(l?.Unidades ?? l?.unidades);
      merged.set(k, (merged.get(k) || 0) + uds);
    }
  }
  const lineasPayload = [];
  for (const [k, uds] of merged.entries()) {
    const [codFamTallaH, codTallaH, codFamTallaV, codTallaV] = k.split("|");
    lineasPayload.push({
      codFamTallaH,
      codTallaH,
      codFamTallaV,
      codTallaV,
      unidades: matrizToNumber(uds),
    });
  }
  return lineasPayload;
}

/** Persiste una fila por pedido (no sumar en la misma celda: eso duplicaba uds al recargar). */
export function flattenMultiPedidoLineasPayload(pedidoLineasByPedido = {}) {
  const lineasPayload = [];
  for (const [idPedRaw, arr] of Object.entries(pedidoLineasByPedido || {})) {
    const idPed = Number(idPedRaw);
    const idPresupuestoA3Pedido =
      Number.isFinite(idPed) && idPed > 0 ? idPed : null;
    for (const l of Array.isArray(arr) ? arr : []) {
      const k = lineaEnvioMergeKey(l);
      if (!k) continue;
      const uds = matrizToNumber(l?.Unidades ?? l?.unidades);
      if (uds < 0) continue;
      const [codFamTallaH, codTallaH, codFamTallaV, codTallaV] = k.split("|");
      lineasPayload.push({
        idPresupuestoA3Pedido,
        codFamTallaH,
        codTallaH,
        codFamTallaV,
        codTallaV,
        unidades: uds,
      });
    }
  }
  return lineasPayload;
}

export function lineasEnvioForPedido(lineas = [], idPed) {
  const id = Number(idPed);
  const list = Array.isArray(lineas) ? lineas : [];
  if (!Number.isFinite(id) || id <= 0) return [];
  return list.filter(
    (l) => Number(l?.IdPresupuestoA3Pedido ?? l?.idPresupuestoA3Pedido) === id,
  );
}

/**
 * Campos logísticos compartidos envío ↔ cabeceras por pedido (misma lista que
 * syncAllPedidoCabecerasLogisticsFromEnvio en el back). No incluye cajas/kg/cbm
 * ni PO propios de cada pedido.
 */
export const ENVIO_SHARED_LOGISTICS_HEADER_KEYS = [
  "ShipmentMethod",
  "BookingDone",
  "InspectionDate",
  "ExFD",
  "ETDF",
  "CutOff",
  "ETD",
  "DD",
  "Forwarder",
  "ShipmentCodeCustomer",
  "BLAWBNr",
  "ETA",
  "DocumentsApproved",
  "OriginalTelexDocsSemt",
];

/** Extrae solo las claves compartidas presentes en `header` (own keys). */
export function pickSharedEnvioLogisticsFields(header = {}) {
  const out = {};
  for (const key of ENVIO_SHARED_LOGISTICS_HEADER_KEYS) {
    if (Object.prototype.hasOwnProperty.call(header, key)) {
      out[key] = header[key];
    }
  }
  return out;
}

/**
 * Copia campos compartidos de la cabecera del envío a cada mini-cabecera.
 * Evita que al guardar pedidos agrupados el recalc del back pise fechas/método
 * con valores obsoletos del estado local.
 */
export function applySharedEnvioHeaderToPedidoHeaders(
  pedidoHeaders = {},
  header = {},
) {
  const shared = pickSharedEnvioLogisticsFields(header);
  const next = {};
  for (const [id, row] of Object.entries(pedidoHeaders || {})) {
    next[id] = { ...(row || {}), ...shared };
  }
  return next;
}

export function buildPedidoCabecerasSavePayload(pedidoHeaders = {}) {
  return Object.values(pedidoHeaders || {})
    .map((r) => ({
      IdPresupuestoA3Pedido: Number(r?.IdPresupuestoA3Pedido),
      NCartons: parseOptionalInt(r.NCartons),
      Unidades:
        String(r?.Unidades ?? "").trim() !== ""
          ? matrizToNumber(r.Unidades)
          : null,
      GrossWeightKgs: parseOptionalDecimal(r.GrossWeightKgs),
      TotalVolumeCbm: parseOptionalDecimal(r.TotalVolumeCbm),
      ETA: apiDateFromInput(r.ETA),
      BookingDone: apiDateFromInput(r.BookingDone),
      InspectionDate: apiDateFromInput(r.InspectionDate),
      ExFD: apiDateFromInput(r.ExFD),
      ETDF: apiDateFromInput(r.ETDF),
      CutOff: apiDateFromInput(r.CutOff),
      ETD: apiDateFromInput(r.ETD),
      DD: apiDateFromInput(r.DD),
      DocumentsApproved: apiDateFromInput(r.DocumentsApproved),
      OriginalTelexDocsSemt: apiDateFromInput(r.OriginalTelexDocsSemt),
      ShipmentMethod: normStr(r.ShipmentMethod) || null,
      Forwarder: normStr(r.Forwarder) || null,
      ShipmentCodeCustomer: normStr(r.ShipmentCodeCustomer) || null,
      BLAWBNr: normStr(r.BLAWBNr) || null,
      Remarks: normStr(r.Remarks) || null,
      POReference: normStr(r.POReference) || null,
    }))
    .filter(
      (r) =>
        Number.isFinite(r.IdPresupuestoA3Pedido) && r.IdPresupuestoA3Pedido > 0,
    );
}
